/**
 * Transcripts API Service
 *
 * API client functions for transcript data access including segments,
 * speakers, and search functionality for synchronized audio playback.
 */

import { apiClient } from "./api";
import type {
  TranscriptResponse,
  TranscriptWithSegmentsResponse,
  TranscriptSegmentResponse,
  SpeakerResponse,
  SpeakerSummary,
  ConversationSentimentResponse,
} from "@/types/transcript";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Get transcript for a recording (without segments)
 */
export async function getTranscript(
  recordingId: string
): Promise<TranscriptResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/transcript`
  );
  return response.data;
}

/**
 * Get transcript with all segments included
 */
export async function getTranscriptWithSegments(
  recordingId: string
): Promise<TranscriptWithSegmentsResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/transcript/full`
  );
  return response.data;
}

/**
 * Get transcript segments with optional time range filtering
 */
export async function getTranscriptSegments(
  recordingId: string,
  options?: {
    startMs?: number;
    endMs?: number;
    limit?: number;
    offset?: number;
  }
): Promise<TranscriptSegmentResponse[]> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/transcript/segments`,
    {
      params: {
        start_ms: options?.startMs,
        end_ms: options?.endMs,
        limit: options?.limit,
        offset: options?.offset,
      },
    }
  );
  return response.data;
}

/**
 * Get the segment active at a specific timestamp
 */
export async function getSegmentAtTime(
  recordingId: string,
  timeMs: number
): Promise<TranscriptSegmentResponse | null> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/transcript/at/${timeMs}`
  );
  return response.data;
}

/**
 * Search within transcript text
 */
export async function searchTranscript(
  recordingId: string,
  query: string,
  limit?: number
): Promise<TranscriptSegmentResponse[]> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/transcript/search`,
    {
      params: { q: query, limit },
    }
  );
  return response.data;
}

/**
 * Get all speakers for a recording
 */
export async function getSpeakers(
  recordingId: string
): Promise<SpeakerResponse[]> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/speakers`
  );
  return response.data;
}

/**
 * Get speaker summary with statistics
 */
export async function getSpeakerSummary(
  recordingId: string
): Promise<SpeakerSummary> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/speakers/summary`
  );
  return response.data;
}

/**
 * Get conversation-level sentiment analysis for a recording
 */
export async function getConversationSentiment(
  recordingId: string
): Promise<ConversationSentimentResponse | null> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/transcript/sentiment`
  );
  return response.data;
}

/**
 * Update speaker custom name
 */
export async function updateSpeaker(
  speakerId: string,
  customName: string | null
): Promise<SpeakerResponse> {
  const response = await apiClient.patch(
    `${API_PREFIX}/speakers/${speakerId}`,
    {
      custom_name: customName,
    }
  );
  return response.data;
}
